import React from 'react';
import type { LuminarySettings, MediaSize } from '@lumen5/luminary';
import Luminary from '@lumen5/luminary';

import WebGlContextLostModal from 'js/src/components/common/WebGlContextLostModal';
import WebGlSupportMissingModal from 'js/src/components/common/WebGLSupportMissingModal';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Flags from 'js/src/libs/flags';
import { DEFAULT_LUMINARY_CONFIG } from 'js/src/libs/video-data-consts';

export const LuminaryContext = React.createContext<Luminary>(null as unknown as Luminary);
export const useLuminaryContext = () => {
    const luminary = React.useContext(LuminaryContext);
    if (!luminary) {
        throw new Error('useLuminaryContext must be used within a LuminaryContextProvider');
    }
    return luminary;
};

const isPropsWithLuminary = (props: Props): props is PropsWithLuminary => {
    return (props as PropsWithLuminary).luminary !== undefined;
};
const isPropsWithoutLuminary = (props: Props): props is PropsWithoutLuminary => {
    return (props as PropsWithoutLuminary).minDimensionResolution !== undefined;
};

type BaseProps = {
    mediaSizeToUse?: MediaSize;
    fetchMedia?: boolean;
    fetchFonts?: boolean;
    fetchAudio?: boolean;
    fetchTransitions?: boolean;
    drawsToCanvas?: boolean;
    loadStatusMaxCount?: number;
    disableRenderer?: boolean;
    disableCaptions?: boolean;
    preserveAssetIds?: boolean;
    autoDraw?: boolean;
    enableHelpers?: boolean;
    name?: string;
    children: React.ReactNode;
};

type PropsWithLuminary = BaseProps & {
    luminary: Luminary;
};

type PropsWithoutLuminary = BaseProps & {
    minDimensionResolution: number;
};

type Props = PropsWithLuminary | PropsWithoutLuminary;

/**
 * The LuminaryContextProvider creates a ContextProvider and initializes a Luminary instance.  The
 * LuminaryContextProvider should be consumed using the `luminaryReduxConnect` method below which allows for behaviour
 * like react-redux, passing in state and operations as React props.
 *
 * A <LuminaryContextProvider> object should be initialized wherever a video boundary lies.
 *
 * Note that EACH <LuminaryContextProvider> instance creates a new WebGL context which are browser-limited (typically
 * to 6-8 for all tabs on the same domain).  We should limit the number of <LuminaryContextProvider> instances
 * as much as possible!
 *
 * Sample code:
 * ```
 *  <LuminaryContextProvider
 *      minDimensionResolution={215}
 *  >
 *      <App/>
 *  </LuminaryContextProvider>
 * ```
 */
export const LuminaryContextProvider = React.memo(function LuminaryContextProvider(props: Props) {
    const {
        mediaSizeToUse,
        fetchMedia,
        fetchFonts,
        fetchAudio,
        fetchTransitions,
        drawsToCanvas,
        loadStatusMaxCount,
        disableRenderer,
        disableCaptions,
        preserveAssetIds,
        autoDraw,
        enableHelpers,
        name,
        children,
    } = props;
    const luminaryFromProps = isPropsWithLuminary(props) ? props.luminary : null;
    const propsIsWithouLuminary = isPropsWithoutLuminary(props);

    const showWebGlSupportMissingModal =
        !disableRenderer && !Luminary.isWebGLSupported && !Luminary.isSlowWebGLSupported;
    const [showWebGlContextLostModal, setShowWebGlContextLostModal] = React.useState(false);
    const [rendererInitFailed, setRendererInitFailed] = React.useState(false);

    const handleWebGLContextLostEvent = React.useCallback(() => {
        setShowWebGlContextLostModal(true);
    }, []);

    const [currentLuminary, setCurrentLuminary] = React.useState<Luminary | null>(() => {
        /*
        Some displays are high density/retina, which means they have more than one physical pixel per
        "virtual pixel" – if we generate a larger preview, the image will look better.  For example,
        modern Macs have pixel densities of 2x.  If the device pixel ratio is larger than one, scale
        the preview!
        */
        const rendererDevicePixelRatio = window.devicePixelRatio;

        /*
        Luminary enforces a max of 1080 for the resolution, we should make sure we don't exceed that.
        */
        const resolution = (() => {
            if (isPropsWithLuminary(props)) {
                return props.luminary.resolutionValue;
            }
            if (isPropsWithoutLuminary(props)) {
                return Math.min(props.minDimensionResolution, 1080);
            }

            throw new Error(
                'LuminaryContextProvider requires prop luminary or minDimensionResolution',
            );
        })();

        const config: LuminarySettings = {
            ...DEFAULT_LUMINARY_CONFIG,
            name: name || 'luminaryContextProvider',
            resolutionValue: resolution,
            rendererDevicePixelRatio: rendererDevicePixelRatio,
        };

        if (mediaSizeToUse !== undefined) {
            config.mediaSizeToUse = mediaSizeToUse;
        }
        if (
            fetchAudio !== undefined ||
            fetchFonts !== undefined ||
            fetchMedia !== undefined ||
            fetchTransitions !== undefined
        ) {
            config.fetchResources = {
                audio: !!fetchAudio,
                fonts: !!fetchFonts,
                media: !!fetchMedia,
                transitions: !!fetchTransitions,
            };
        }

        if (drawsToCanvas !== undefined) {
            config.drawsToCanvas = drawsToCanvas;
        }
        if (loadStatusMaxCount !== undefined) {
            config.loadStatusMaxCount = loadStatusMaxCount;
        }
        if (disableRenderer !== undefined) {
            config.disableRenderer = disableRenderer;
        }
        if (preserveAssetIds !== undefined) {
            config.preserveAssetIds = preserveAssetIds;
        }
        if (autoDraw !== undefined) {
            config.autoDraw = autoDraw;
        }
        if (enableHelpers !== undefined) {
            config.enableHelpers = enableHelpers;
        }
        if (disableCaptions !== undefined) {
            config.disableCaptions = disableCaptions;
        }

        config.onRendererInitFailed = () => {
            ErrorReporter.capture(new Error('WebGL renderer failed to initialize'));
            setRendererInitFailed(true);
        };

        config.enableEnhancedAssetTransform = Flags.canUseEnhancedAssetTransform();

        if (
            !config.disableRenderer &&
            !Luminary.isWebGLSupported &&
            !Luminary.isSlowWebGLSupported
        ) {
            return null;
        }

        if (luminaryFromProps) {
            return luminaryFromProps;
        }

        // window.Luminary is a UMD namespace { __esModule: true, default: Class, ... }
        // The rspack external unwraps .default, so `Luminary` here is the class.
        // Compare against .default to detect if it's the same class identity.
        const windowLuminaryNs = window.Luminary as unknown as {
            default?: typeof Luminary;
            __esModule?: boolean;
        };
        const WindowLuminaryClass = windowLuminaryNs?.default;

        try {
            ErrorReporter.addBreadcrumb({
                category: 'luminary.init',
                message: 'Creating new Luminary instance',
                data: {
                    configName: config.name,
                    classIdentityMatch: WindowLuminaryClass === Luminary,
                },
            });
            return new Luminary(config);
        } catch (error) {
            // Proxy-construct detection: if an extension wraps Luminary in a
            // Proxy with a construct trap that uses a different newTarget, the
            // resulting instance won't carry the private-field brand even though
            // classIdentityMatch is true. These diagnostics test for that.
            const proxyDiagnostics = (() => {
                try {
                    // 1. Own property names on the class — a Proxy handler may
                    //    leak extra properties or be missing expected ones
                    const ownProps = Object.getOwnPropertyNames(Luminary).sort().join(',');

                    // 2. toString() representation — native classes start with
                    //    "class", Proxies may differ in some engines
                    const classToString = Function.prototype.toString.call(Luminary).slice(0, 50);

                    // 3. Prototype chain integrity — walk the class chain to
                    //    detect if the parent hierarchy was tampered with
                    const protoChain: string[] = [];
                    let proto = Object.getPrototypeOf(Luminary);
                    while (proto && protoChain.length < 5) {
                        protoChain.push(proto.name || 'anonymous');
                        proto = Object.getPrototypeOf(proto);
                    }

                    // 4. Check if prototype.constructor round-trips — a Proxy
                    //    may break this identity
                    const protoCtorMatch = Luminary.prototype?.constructor === Luminary;

                    // 5. Symbol.toStringTag can expose wrapper identity
                    const toStringTag = (Luminary as unknown as { [Symbol.toStringTag]?: string })[
                        Symbol.toStringTag
                    ];

                    // 6. Check for extra/unexpected keys on the UMD namespace
                    //    that would indicate extension tampering
                    const nsKeys = Object.keys(windowLuminaryNs || {}).join(',');

                    return {
                        ownProps,
                        classToString,
                        protoChain: protoChain.join(' → '),
                        protoCtorMatch,
                        toStringTag: toStringTag ?? 'none',
                        nsKeys,
                    };
                } catch (e) {
                    return { diagnosticError: String(e).slice(0, 200) };
                }
            })();

            ErrorReporter.addBreadcrumb({
                category: 'luminary.init',
                message: 'Luminary constructor failed',
                level: 'error',
                data: {
                    error: String(error),
                    errorName: error instanceof Error ? error.name : typeof error,
                    classIdentityMatch: WindowLuminaryClass === Luminary,
                    ...proxyDiagnostics,
                },
            });

            throw error;
        }
    });

    React.useEffect(() => {
        if (!disableRenderer && !Luminary.isWebGLSupported && !Luminary.isSlowWebGLSupported) {
            analytics.track('WebGL is not supported');
        }
    }, [disableRenderer]);

    React.useEffect(() => {
        if (luminaryFromProps && luminaryFromProps !== currentLuminary) {
            setCurrentLuminary(luminaryFromProps);
        }
    }, [luminaryFromProps, currentLuminary]);

    React.useEffect(() => {
        const luminary = currentLuminary;
        const shouldDestroy = propsIsWithouLuminary;
        luminary?.renderer?.view?.addEventListener('webglcontextlost', handleWebGLContextLostEvent);

        return () => {
            luminary?.renderer?.view?.removeEventListener(
                'webglcontextlost',
                handleWebGLContextLostEvent,
            );
            if (shouldDestroy) {
                luminary?.destroy();
            }
        };
    }, [currentLuminary, propsIsWithouLuminary, handleWebGLContextLostEvent]);

    if (!currentLuminary) {
        return (
            <>
                <WebGlSupportMissingModal
                    showing={showWebGlSupportMissingModal || rendererInitFailed}
                />
                <WebGlContextLostModal showing={showWebGlContextLostModal} />
            </>
        );
    }

    return (
        <LuminaryContext value={currentLuminary}>
            {children}
            <WebGlSupportMissingModal
                showing={showWebGlSupportMissingModal || rendererInitFailed}
            />
            <WebGlContextLostModal showing={showWebGlContextLostModal} />
        </LuminaryContext>
    );
});
